import sessionsUpdate from 'fetchers/sessions/update';
import AuthData from 'lib/authData';

/**
 * CD-8740 — single-flight wrapper around `PUT /sessions`.
 *
 * oso-api's refresh is a **single-use rotation**: every successful
 * `PUT /sessions` clears all of the user's refresh tokens and mints exactly one
 * new one (`Services::Auth.refresh_token_for`). Two concurrent refreshes with
 * the same `access_refresh` cookie therefore race — the first to commit wins,
 * the second validates a token that no longer exists and 401s, which the boot
 * probe and the hourly saga both rightly treat as "session gone".
 *
 * That race is real, not theoretical: "restore all tabs" after a browser
 * restart boots every tab at once, and each one probes. Without coordination,
 * every restored tab but one renders signed out while the browser demonstrably
 * holds a live session.
 *
 * Two layers of defence, both per-origin and cross-tab:
 *
 * 1. **Mutex** — the Web Locks API serialises refreshes under
 *    `REFRESH_LOCK_NAME`, so two refreshes never race server-side. Where
 *    `navigator.locks` does not exist (jsdom, very old browsers) we fall back
 *    to today's uncoordinated behaviour rather than breaking refresh entirely.
 * 2. **Dedupe** — inside the lock, a tab that can see another tab refreshed
 *    within `REFRESH_DEDUPE_WINDOW_MS` skips the network call and resolves with
 *    the profile that refresh cached. Sound because everything a refresh
 *    produces is shared between tabs already: the rotated cookies live in the
 *    browser's jar and the profile in `AuthData`'s localStorage. Without this,
 *    N restored tabs would perform N back-to-back rotations — safe once
 *    serialised, but pointless load and churn.
 *
 * A failed refresh records nothing, so a waiting tab retries for real rather
 * than trusting a timestamp the failure never earned.
 */
export const REFRESH_LOCK_NAME = 'oso-session-refresh';
export const REFRESH_DEDUPE_WINDOW_MS = 10000;
export const REFRESHED_AT_KEY = 'oso-session-refreshed-at';

type SessionResult = { user?: unknown };

const readRefreshedAt = (): number => {
  try {
    const raw = window.localStorage.getItem(REFRESHED_AT_KEY);
    const at = Number(raw);
    return Number.isFinite(at) ? at : 0;
  } catch (_e) {
    return 0; // localStorage unavailable — treat as never refreshed.
  }
};

const recordRefreshedAt = (): void => {
  try {
    window.localStorage.setItem(REFRESHED_AT_KEY, String(Date.now()));
  } catch (_e) {
    // localStorage unavailable — dedupe degrades to "always refresh", which is
    // exactly the pre-lock behaviour and still correct once serialised.
  }
};

/**
 * The profile another tab's just-completed refresh left in `AuthData`, or null
 * if no refresh completed recently enough (or left nothing usable) — in which
 * case the caller must do a real refresh.
 */
const recentlyRefreshedProfile = (): SessionResult | null => {
  if (Date.now() - readRefreshedAt() >= REFRESH_DEDUPE_WINDOW_MS) return null;

  try {
    const cached = AuthData.get() as SessionResult | undefined;
    return cached?.user ? cached : null;
  } catch (_e) {
    return null;
  }
};

const refreshOrReuse = (opts: object): Promise<SessionResult> => {
  const reusable = recentlyRefreshedProfile();
  if (reusable) return Promise.resolve(reusable);

  return sessionsUpdate(opts).then((result: SessionResult) => {
    recordRefreshedAt();
    return result;
  });
};

type LockManagerLike = {
  request: (name: string, callback: () => Promise<SessionResult>) => Promise<SessionResult>;
};

/**
 * Refresh the session, coordinated across tabs. Drop-in for calling
 * `sessionsUpdate` directly: resolves with the session result (fresh from the
 * server, or reused from a concurrent tab's refresh) and rejects with exactly
 * what `sessionsUpdate` rejects with.
 *
 * @param opts callApi options, passed straight through — e.g. the local
 *   `onError` override the boot probe and the refresh saga both rely on.
 */
const refreshSession = (opts: object = {}): Promise<SessionResult> => {
  const locks = (typeof navigator !== 'undefined'
    ? (navigator as { locks?: LockManagerLike }).locks
    : undefined);

  if (!locks?.request) return refreshOrReuse(opts);

  return locks.request(REFRESH_LOCK_NAME, () => refreshOrReuse(opts));
};

export default refreshSession;
